import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Confirm, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch, type Tone, TableWrap } from '../components/ui.tsx';
import { locale, t, tn, type MessageKey } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { dateTime, day as dayText, elapsed, money, parseMoney } from '../lib/format.ts';
import { useUi } from '../lib/ui-state.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { downloadCsv, Kpi, plain, Table } from './Reports.tsx';

/**
 * Maintenance: anyone reports a problem with a machine; the maintenance team works the requests on a
 * board, keeps the equipment and the preventive plans, and sees the calendar and what it cost.
 */

type Stage = 'new' | 'in_progress' | 'waiting' | 'done' | 'cancelled';
type Priority = 'low' | 'normal' | 'high' | 'urgent';
type Kind = 'corrective' | 'preventive';
type Tab = 'board' | 'equipment' | 'plans' | 'calendar' | 'teams' | 'report';

interface Row {
  id: string;
  number: number;
  name: string;
  title: string;
  kind: Kind;
  priority: Priority;
  stage: Stage;
  equipment: string | null;
  equipment_name: string | null;
  equipment_code: string | null;
  place: string | null;
  stopped: boolean;
  team: string | null;
  team_name: string | null;
  technician: string | null;
  technician_name: string | null;
  reported_by: string | null;
  reported_by_name: string | null;
  scheduled: string | null;
  duration: number;
  spent: number;
  cost: number;
  plan: string | null;
  checks: number;
  checks_done: number;
  created_at: string;
  started_at: string | null;
  done_at: string | null;
}
interface Detail extends Row {
  description: string | null;
  resolution: string | null;
  checklist: { text: string; done: boolean }[];
  downtime: number | null;
  events: { kind: string; text: string | null; at: string; by_name: string | null }[];
  today: string;
  can: { work: boolean; edit: boolean };
}
interface Overview {
  today: string;
  kpi: { open: number; urgent: number; late: number; mine: number; stopped: number; done_30: number; preventive_30: number; mttr: number | null };
  mine: Row[];
  week: { day: string; n: number }[];
  warranty: { id: string; name: string; warranty_until: string }[];
  can: { work: boolean; manage: boolean };
}
interface Choices {
  equipment: { id: string; name: string; code: string | null; category: string | null; place: string | null }[];
  teams: { id: string; name: string }[];
  workers: { id: string; name: string }[];
  locations: { id: string; name: string }[];
  categories: string[];
}

const STAGES: Stage[] = ['new', 'in_progress', 'waiting', 'done'];
const STAGE: Record<Stage, { tone: Tone; icon: IconName }> = {
  new: { tone: 'info', icon: 'bell' },
  in_progress: { tone: 'primary', icon: 'wrench' },
  waiting: { tone: 'warn', icon: 'clock' },
  done: { tone: 'ok', icon: 'checkCircle' },
  cancelled: { tone: 'neutral', icon: 'x' },
};
const PRIORITIES: Priority[] = ['low', 'normal', 'high', 'urgent'];
const PRIORITY_TONE: Record<Priority, Tone> = { low: 'neutral', normal: 'info', high: 'warn', urgent: 'danger' };
const hm = (min: number) => (min < 60 ? t('mt.min', { n: min }) : min % 60 ? t('mt.h_min', { h: Math.floor(min / 60), m: min % 60 }) : t('mt.hours', { n: min / 60 }));
const hours = (h: number | null) => (h === null ? '—' : t('mt.hours', { n: new Intl.NumberFormat(locale(), { maximumFractionDigits: 1 }).format(h) }));
const initials = (n: string | null) =>
  n
    ? n
        .split(/\s+/)
        .slice(0, 2)
        .map((x) => x[0])
        .join('')
    : '?';
const isOpen = (s: Stage) => s === 'new' || s === 'in_progress' || s === 'waiting';
const addDaysIso = (d: string, n: number) => new Date(Date.parse(`${d}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);

export function Maintenance() {
  const [params, setParams] = useSearchParams();
  const { data: o, reload } = useAction<Overview>('maint.overview');
  const [version, setVersion] = useState(0);
  const [open, setOpen] = useState<string | null>(null);
  const [machine, setMachine] = useState<string | null>(null);
  const [report, setReport] = useState<{ equipment?: string } | null>(null);
  const work = !!o?.can.work;
  const manage = !!o?.can.manage;
  const tabs: { key: Tab; icon: IconName }[] = work
    ? [
        { key: 'board', icon: 'layers' },
        { key: 'equipment', icon: 'wrench' },
        { key: 'plans', icon: 'calendar' },
        { key: 'calendar', icon: 'list' },
        ...(manage
          ? ([
              { key: 'teams', icon: 'users' },
              { key: 'report', icon: 'chart' },
            ] as const)
          : []),
      ]
    : [];
  const tab = (tabs.some((x) => x.key === params.get('tab')) ? params.get('tab') : 'board') as Tab;
  const setTab = (k: Tab) => {
    const p = new URLSearchParams(params);
    if (k === 'board') p.delete('tab');
    else p.set('tab', k);
    setParams(p, { replace: true });
  };
  const changed = async () => {
    setVersion((v) => v + 1);
    await reload();
  };
  return (
    <Shell>
      <div className="page reports mt-page">
        <div className="page-head">
          <div>
            <h1>{t('mt.title')}</h1>
            <p className="muted">{work ? t('mt.subtitle_work') : t('mt.subtitle')}</p>
          </div>
          <Button variant="primary" icon="alert" onClick={() => setReport({})}>
            {t('mt.report_problem')}
          </Button>
        </div>
        {tabs.length ? (
          <div className="segmented tabs" role="tablist" aria-label={t('mt.sections')}>
            {tabs.map((x) => (
              <button key={x.key} role="tab" aria-selected={tab === x.key} className={tab === x.key ? 'active' : ''} onClick={() => setTab(x.key)}>
                <Icon name={x.icon} size={16} />
                {t(`mt.tab_${x.key}` as MessageKey)}
              </button>
            ))}
          </div>
        ) : null}
        {!o ? (
          <Skeleton h={300} r="var(--radius-lg)" />
        ) : !work ? (
          <Mine o={o} onOpen={setOpen} onReport={() => setReport({})} />
        ) : tab === 'board' ? (
          <BoardTab key={version} o={o} onOpen={setOpen} onChange={changed} />
        ) : tab === 'equipment' ? (
          <EquipmentTab key={version} manage={manage} onOpen={setMachine} />
        ) : tab === 'plans' ? (
          <PlansTab key={version} manage={manage} onChange={changed} />
        ) : tab === 'calendar' ? (
          <CalendarTab key={version} today={o.today} onOpen={setOpen} />
        ) : tab === 'teams' ? (
          <TeamsTab onChange={changed} />
        ) : (
          <ReportTab today={o.today} />
        )}
      </div>
      {open ? <RequestDialog id={open} onClose={() => setOpen(null)} onChange={changed} onMachine={(id) => (setOpen(null), setMachine(id))} /> : null}
      {machine ? (
        <EquipmentDialog
          id={machine}
          onClose={() => setMachine(null)}
          onChange={changed}
          onOpen={(id) => (setMachine(null), setOpen(id))}
          onReport={(id) => (setMachine(null), setReport({ equipment: id }))}
        />
      ) : null}
      {report ? <ReportDialog equipment={report.equipment} onClose={() => setReport(null)} onDone={changed} /> : null}
    </Shell>
  );
}

// ---- everyone: report a problem and follow it -------------------------------------------

function Mine({ o, onOpen, onReport }: { o: Overview; onOpen: (id: string) => void; onReport: () => void }) {
  if (!o.mine.length)
    return (
      <section className="panel">
        <EmptyState
          icon="wrench"
          title={t('mt.mine_empty')}
          body={t('mt.mine_empty_body')}
          action={
            <Button variant="primary" icon="alert" onClick={onReport}>
              {t('mt.report_problem')}
            </Button>
          }
        />
      </section>
    );
  return (
    <section className="panel">
      <h2>{t('mt.your_reports')}</h2>
      <ul className="mt-mine">
        {o.mine.map((r) => (
          <li key={r.id}>
            <button type="button" onClick={() => onOpen(r.id)}>
              <span className="mt-mine-main">
                <strong>{r.title}</strong>
                <span className="muted small">
                  {r.name} · {r.equipment_name ?? r.place} · {dateTime(r.created_at)}
                </span>
                {r.technician_name ? <span className="small">{t('mt.with_who', { name: r.technician_name })}</span> : null}
              </span>
              <StatusBadge tone={STAGE[r.stage].tone} icon={STAGE[r.stage].icon} label={t(`mt.stage_${r.stage}` as MessageKey)} />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function ReportDialog({ equipment, onClose, onDone }: { equipment?: string; onClose: () => void; onDone: () => Promise<void> }) {
  const { data: c } = useAction<Choices>('maint.choices');
  const { busy, run } = useSave();
  const [f, setF] = useState({ equipment: equipment ?? '', place: '', title: '', description: '', stopped: false, priority: 'normal' as Priority });
  const [tried, setTried] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const errors = {
    equipment: !f.equipment && !f.place.trim() ? t('mt.e_where') : null,
    title: f.title.trim().length < 3 ? t('mt.e_title') : null,
  };
  const places = [...new Set((c?.equipment ?? []).map((e) => e.place ?? t('mt.no_place')))];
  const save = () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    void run(async () => {
      const r = await action<{ name: string }>('maint.report_problem', {
        equipment: f.equipment || null,
        place: f.place.trim() || null,
        title: f.title.trim(),
        description: f.description.trim() || null,
        stopped: f.stopped,
        priority: f.priority,
      });
      await onDone();
      onClose();
      return r;
    }, t('mt.report_sent'));
  };
  return (
    <Dialog
      title={t('mt.report_problem')}
      onClose={onClose}
      size="md"
      footer={
        <span className="foot-actions">
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="send" busy={busy} onClick={save}>
            {t('mt.send_report')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        <Field label={t('mt.machine')} hint={t('mt.machine_hint')} error={tried ? errors.equipment : null}>
          <select className="input" value={f.equipment} onChange={(e) => set('equipment', e.target.value)} data-autofocus>
            <option value="">{t('mt.no_machine')}</option>
            {places.map((p) => (
              <optgroup key={p} label={p}>
                {(c?.equipment ?? [])
                  .filter((e) => (e.place ?? t('mt.no_place')) === p)
                  .map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.code ? `${e.name} (${e.code})` : e.name}
                    </option>
                  ))}
              </optgroup>
            ))}
          </select>
        </Field>
        {!f.equipment ? (
          <Field label={t('mt.where')} hint={t('mt.where_hint')} error={tried ? errors.equipment : null}>
            <input className="input" value={f.place} maxLength={80} onChange={(e) => set('place', e.target.value)} />
          </Field>
        ) : null}
        <Field label={t('mt.what_wrong')} error={tried ? errors.title : null} required>
          <input className="input" value={f.title} maxLength={120} placeholder={t('mt.what_wrong_example')} onChange={(e) => set('title', e.target.value)} />
        </Field>
        <Field label={t('mt.details')} hint={t('mt.details_hint')}>
          <textarea className="input" rows={3} value={f.description} maxLength={2000} onChange={(e) => set('description', e.target.value)} />
        </Field>
        <div className="setting-row">
          <div>
            <strong>{t('mt.stopped')}</strong>
            <p className="muted">{t('mt.stopped_hint')}</p>
          </div>
          <Switch checked={f.stopped} onChange={(x) => set('stopped', x)} label={t('mt.stopped')} />
        </div>
        <Field label={t('mt.priority')}>
          <PriorityPick value={f.priority} onChange={(p) => set('priority', p)} />
        </Field>
      </div>
    </Dialog>
  );
}

function PriorityPick({ value, onChange }: { value: Priority; onChange: (p: Priority) => void }) {
  return (
    <div className="segmented mt-prio" role="radiogroup" aria-label={t('mt.priority')}>
      {PRIORITIES.map((p) => (
        <button key={p} type="button" role="radio" aria-checked={value === p} className={`${value === p ? 'active' : ''} mt-prio-${p}`} onClick={() => onChange(p)}>
          {t(`mt.prio_${p}` as MessageKey)}
        </button>
      ))}
    </div>
  );
}

// ---- the board --------------------------------------------------------------------------

function RequestCard({ r, today, draggable, onOpen, onDragStart }: { r: Row; today: string; draggable: boolean; onOpen: () => void; onDragStart?: (e: React.DragEvent) => void }) {
  const late = isOpen(r.stage) && r.scheduled && r.scheduled < today;
  return (
    <article
      className={`tk-task mt-card prio-${r.priority}${r.stopped && isOpen(r.stage) ? ' stopped' : ''}${!isOpen(r.stage) ? ' done' : ''}`}
      draggable={draggable}
      onDragStart={onDragStart}
      data-id={r.id}
    >
      <button className="tk-task-main" onClick={onOpen}>
        <span className="mt-card-top small">
          <span className="muted tk-task-no">{r.name}</span>
          {r.kind === 'preventive' ? (
            <span className="mt-chip">
              <Icon name="calendar" size={12} /> {t('mt.preventive')}
            </span>
          ) : null}
          {r.priority === 'urgent' || r.priority === 'high' ? <span className={`mt-chip ${r.priority}`}>{t(`mt.prio_${r.priority}` as MessageKey)}</span> : null}
        </span>
        <strong>{r.title}</strong>
        <span className="small muted mt-card-where">
          <Icon name="wrench" size={12} /> {[r.equipment_name, r.place].filter(Boolean).join(' · ')}
        </span>
        {r.stopped && isOpen(r.stage) ? (
          <span className="mt-stopped small">
            <Icon name="alert" size={12} /> {t('mt.stopped_for', { time: elapsed(r.created_at) })}
          </span>
        ) : null}
        <span className="tk-task-meta small">
          {r.scheduled ? (
            <span className={late ? 'tk-warn' : 'muted'}>
              <Icon name="calendar" size={12} /> {dayText(r.scheduled)}
            </span>
          ) : null}
          {r.checks ? (
            <span className={r.checks_done === r.checks ? 'text-ok' : 'muted'}>
              <Icon name="check" size={12} /> {r.checks_done}/{r.checks}
            </span>
          ) : null}
          {r.spent ? (
            <span className="muted">
              <Icon name="clock" size={12} /> {hm(r.spent)}
            </span>
          ) : null}
          <span className="tk-avatar" title={r.technician_name ?? t('mt.nobody')} aria-label={r.technician_name ?? t('mt.nobody')}>
            {initials(r.technician_name)}
          </span>
        </span>
      </button>
    </article>
  );
}

function BoardTab({ o, onOpen, onChange }: { o: Overview; onOpen: (id: string) => void; onChange: () => Promise<void> }) {
  const { fail } = useUi();
  const { data: c } = useAction<Choices>('maint.choices');
  const [who, setWho] = useState<'' | 'me' | 'nobody'>('');
  const [team, setTeam] = useState('');
  const [kind, setKind] = useState<'' | Kind>('');
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setSearch(q.trim()), 250);
    return () => clearTimeout(id);
  }, [q]);
  const { data, reload } = useAction<{ today: string; requests: Row[] }>('maint.requests', {
    technician: who || null,
    team: team || null,
    kind: kind || null,
    search: search || null,
  });
  const [rows, setRows] = useState<Row[]>([]);
  useEffect(() => setRows(data?.requests ?? []), [data]);
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<Stage | null>(null);
  const [ask, setAsk] = useState<{ id: string; stage: Stage } | null>(null);
  const moveTo = async (id: string, stage: Stage, note: string | null = null) => {
    const prev = rows;
    setRows((all) => all.map((r) => (r.id === id ? { ...r, stage } : r)));
    try {
      await action('maint.move', { id, stage, note });
      await reload();
      await onChange();
    } catch (e) {
      setRows(prev);
      fail(e);
    }
  };
  const drop = (stage: Stage) => {
    const id = dragging;
    setDragging(null);
    setOver(null);
    const r = rows.find((x) => x.id === id);
    if (!r || r.stage === stage) return;
    // Waiting and done ask for a word; the others move at once.
    if (stage === 'waiting' || stage === 'done') setAsk({ id: r.id, stage });
    else void moveTo(r.id, stage);
  };
  const k = o.kpi;
  return (
    <>
      <section className="rep-kpis" aria-label={t('mt.glance')}>
        <Kpi label={t('mt.k_open')} value={String(k.open)} hint={k.mine ? tn('mt.k_open_mine', k.mine) : t('mt.k_open_none_mine')} />
        <Kpi label={t('mt.k_stopped')} value={String(k.stopped)} hint={k.stopped ? t('mt.k_stopped_hint') : t('mt.k_stopped_none')} warn={k.stopped > 0} />
        <Kpi label={t('mt.k_late')} value={String(k.late)} hint={k.urgent ? tn('mt.k_urgent', k.urgent) : t('mt.k_late_hint')} warn={k.late > 0 || k.urgent > 0} />
        <Kpi label={t('mt.k_mttr')} value={hours(k.mttr)} hint={t('mt.k_mttr_hint')} />
        <Kpi label={t('mt.k_done')} value={String(k.done_30)} hint={tn('mt.k_done_hint', k.preventive_30)} />
      </section>
      {o.warranty.length ? (
        <p className="callout mt-callout">
          <Icon name="shield" size={16} />
          <span>
            {t('mt.warranty_soon')} {o.warranty.map((w) => `${w.name} (${dayText(w.warranty_until)})`).join(', ')}
          </span>
        </p>
      ) : null}
      <div className="toolbar tk-board-tools mt-tools">
        <label className="search">
          <Icon name="search" size={16} />
          <input className="input" type="search" value={q} placeholder={t('mt.search')} aria-label={t('mt.search')} onChange={(e) => setQ(e.target.value)} />
        </label>
        <div className="segmented" role="radiogroup" aria-label={t('mt.technician')}>
          {(['', 'me', 'nobody'] as const).map((w) => (
            <button key={w} type="button" role="radio" aria-checked={who === w} className={who === w ? 'active' : ''} onClick={() => setWho(w)}>
              {t(w === '' ? 'mt.everyone' : w === 'me' ? 'mt.mine_filter' : 'mt.unassigned')}
            </button>
          ))}
        </div>
        <select className="input mt-select" aria-label={t('mt.team')} value={team} onChange={(e) => setTeam(e.target.value)}>
          <option value="">{t('mt.all_teams')}</option>
          {(c?.teams ?? []).map((x) => (
            <option key={x.id} value={x.id}>
              {x.name}
            </option>
          ))}
        </select>
        <select className="input mt-select" aria-label={t('mt.kind')} value={kind} onChange={(e) => setKind(e.target.value as '' | Kind)}>
          <option value="">{t('mt.all_kinds')}</option>
          <option value="corrective">{t('mt.corrective')}</option>
          <option value="preventive">{t('mt.preventive')}</option>
        </select>
        <span className="muted small tk-drag-hint">{t('mt.drag_hint')}</span>
      </div>
      {!data ? (
        <Skeleton h={320} r="var(--radius-lg)" />
      ) : (
        <div className="tk-board mt-board" style={{ '--cols': STAGES.length } as React.CSSProperties}>
          {STAGES.map((s) => {
            const cards = rows.filter((r) => (s === 'done' ? r.stage === 'done' || r.stage === 'cancelled' : r.stage === s));
            return (
              <section
                key={s}
                className={`tk-col mt-col-${s}${over === s ? ' over' : ''}${s === 'done' ? ' done' : ''}`}
                aria-label={t(`mt.stage_${s}` as MessageKey)}
                onDragOver={(e) => {
                  if (!dragging) return;
                  e.preventDefault();
                  if (over !== s) setOver(s);
                }}
                onDragLeave={(e) => {
                  if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) setOver(null);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  drop(s);
                }}
              >
                <header className="tk-col-head">
                  <Icon name={STAGE[s].icon} size={16} />
                  <strong>{t(`mt.stage_${s}` as MessageKey)}</strong>
                  <span className="muted small">{cards.length}</span>
                </header>
                <div className="tk-col-body">
                  {cards.map((r) => (
                    <RequestCard
                      key={r.id}
                      r={r}
                      today={o.today}
                      draggable={r.stage !== 'cancelled'}
                      onOpen={() => onOpen(r.id)}
                      onDragStart={(e) => {
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('text/plain', r.id);
                        setDragging(r.id);
                      }}
                    />
                  ))}
                  {!cards.length ? <p className="muted small tk-col-empty">{t(`mt.col_empty_${s}` as MessageKey)}</p> : null}
                </div>
              </section>
            );
          })}
        </div>
      )}
      {ask ? (
        <MoveDialog
          stage={ask.stage}
          onClose={() => setAsk(null)}
          onMove={async (note) => {
            const a = ask;
            setAsk(null);
            await moveTo(a.id, a.stage, note);
          }}
        />
      ) : null}
    </>
  );
}

/** Asks the one word a move needs: what we wait for, what was done, why it is cancelled. */
function MoveDialog({ stage, onClose, onMove }: { stage: Stage; onClose: () => void; onMove: (note: string | null) => Promise<void> }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const needed = stage === 'waiting' || stage === 'cancelled';
  const [tried, setTried] = useState(false);
  const go = async () => {
    setTried(true);
    if (needed && !note.trim()) return;
    setBusy(true);
    await onMove(note.trim() || null);
    setBusy(false);
  };
  return (
    <Dialog
      title={t(`mt.move_title_${stage}` as MessageKey)}
      onClose={onClose}
      size="sm"
      footer={
        <span className="foot-actions">
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant={stage === 'cancelled' ? 'danger' : 'primary'} icon={STAGE[stage].icon} busy={busy} onClick={() => void go()}>
            {t(`mt.move_${stage}` as MessageKey)}
          </Button>
        </span>
      }
    >
      <Field label={t(`mt.move_note_${stage}` as MessageKey)} hint={t(`mt.move_hint_${stage}` as MessageKey)} error={tried && needed && !note.trim() ? t('mt.e_note') : null} required={needed}>
        <textarea className="input" rows={3} value={note} maxLength={2000} onChange={(e) => setNote(e.target.value)} data-autofocus />
      </Field>
    </Dialog>
  );
}

// ---- one request ------------------------------------------------------------------------

function RequestDialog({ id, onClose, onChange, onMachine }: { id: string; onClose: () => void; onChange: () => Promise<void>; onMachine: (id: string) => void }) {
  const { fail } = useUi();
  const { data: r, reload } = useAction<Detail>('maint.request', { id });
  const [edit, setEdit] = useState(false);
  const [ask, setAsk] = useState<Stage | null>(null);
  const [note, setNote] = useState('');
  const { busy, run } = useSave();
  const refresh = async () => {
    await reload();
    await onChange();
  };
  const move = async (stage: Stage, n: string | null = null) => {
    await run(
      async () => {
        await action('maint.move', { id, stage, note: n });
        await refresh();
      },
      t(`mt.moved_${stage}` as MessageKey),
    );
  };
  const tick = async (index: number, done: boolean) => {
    try {
      await action('maint.check', { id, index, done });
      await refresh();
    } catch (e) {
      fail(e);
    }
  };
  const time = (minutes: number) =>
    run(
      async () => {
        await action('maint.add_time', { id, minutes });
        await refresh();
      },
      t('mt.time_added', { time: hm(Math.abs(minutes)) }),
    );
  if (!r)
    return (
      <Dialog title={t('mt.request')} onClose={onClose} size="lg">
        <Skeleton h={320} />
      </Dialog>
    );
  if (edit) return <RequestForm initial={r} onClose={() => setEdit(false)} onSaved={refresh} />;
  const open = isOpen(r.stage);
  const work = r.can.work;
  const late = open && r.scheduled && r.scheduled < r.today;
  const actions: { stage: Stage; primary?: boolean }[] = !work
    ? []
    : r.stage === 'new'
      ? [{ stage: 'in_progress', primary: true }, { stage: 'waiting' }, { stage: 'cancelled' }]
      : r.stage === 'in_progress'
        ? [{ stage: 'done', primary: true }, { stage: 'waiting' }, { stage: 'new' }, { stage: 'cancelled' }]
        : r.stage === 'waiting'
          ? [{ stage: 'in_progress', primary: true }, { stage: 'done' }, { stage: 'new' }, { stage: 'cancelled' }]
          : [{ stage: 'in_progress' }];
  return (
    <Dialog
      title={`${r.name} · ${r.title}`}
      onClose={onClose}
      size="lg"
      footer={
        <span className="foot-actions mt-foot">
          {r.can.edit && open ? (
            <Button variant="ghost" icon="edit" onClick={() => setEdit(true)}>
              {t('mt.edit')}
            </Button>
          ) : null}
          <span className="mt-foot-moves">
            {actions.map((a) => (
              <Button
                key={a.stage}
                variant={a.primary ? 'primary' : a.stage === 'cancelled' ? 'ghost' : 'secondary'}
                icon={a.stage === 'in_progress' && !open ? 'undo' : STAGE[a.stage].icon}
                busy={busy}
                onClick={() => (a.stage === 'waiting' || a.stage === 'done' || a.stage === 'cancelled' ? setAsk(a.stage) : void move(a.stage))}
              >
                {t(a.stage === 'in_progress' ? (open ? (r.stage === 'waiting' ? 'mt.resume' : 'mt.start') : 'mt.reopen') : (`mt.move_${a.stage}` as MessageKey))}
              </Button>
            ))}
          </span>
        </span>
      }
    >
      <div className="mt-detail">
        <div className="mt-badges">
          <StatusBadge tone={STAGE[r.stage].tone} icon={STAGE[r.stage].icon} label={t(`mt.stage_${r.stage}` as MessageKey)} />
          <StatusBadge tone={PRIORITY_TONE[r.priority]} icon="alert" label={t(`mt.prio_${r.priority}` as MessageKey)} />
          <StatusBadge tone="neutral" icon={r.kind === 'preventive' ? 'calendar' : 'wrench'} label={t(`mt.${r.kind}` as MessageKey)} />
          {r.stopped ? (
            <StatusBadge
              tone={open ? 'danger' : 'neutral'}
              icon="alert"
              label={open ? t('mt.stopped_for', { time: hours(r.downtime) }) : t('mt.was_stopped', { time: hours(r.downtime) })}
              pulse={open}
            />
          ) : null}
        </div>
        <dl className="mt-facts">
          <div>
            <dt>{t('mt.machine')}</dt>
            <dd>
              {r.equipment && work ? (
                <button type="button" className="link" onClick={() => onMachine(r.equipment!)}>
                  {r.equipment_name}
                  {r.equipment_code ? ` (${r.equipment_code})` : ''}
                </button>
              ) : (
                (r.equipment_name ?? '—')
              )}
            </dd>
          </div>
          <div>
            <dt>{t('mt.where')}</dt>
            <dd>{r.place ?? '—'}</dd>
          </div>
          <div>
            <dt>{t('mt.technician')}</dt>
            <dd>{r.technician_name ?? t('mt.nobody')}</dd>
          </div>
          <div>
            <dt>{t('mt.team')}</dt>
            <dd>{r.team_name ?? '—'}</dd>
          </div>
          <div>
            <dt>{t('mt.scheduled')}</dt>
            <dd className={late ? 'text-danger' : undefined}>{r.scheduled ? dayText(r.scheduled) : '—'}</dd>
          </div>
          <div>
            <dt>{t('mt.time')}</dt>
            <dd>{t('mt.time_of', { spent: hm(r.spent), planned: hm(r.duration) })}</dd>
          </div>
          <div>
            <dt>{t('mt.reported_by')}</dt>
            <dd>
              {r.reported_by_name ?? t('mt.by_plan')} · {dateTime(r.created_at)}
            </dd>
          </div>
          {r.cost ? (
            <div>
              <dt>{t('mt.cost')}</dt>
              <dd>{money(r.cost)}</dd>
            </div>
          ) : null}
        </dl>
        {r.description ? <p className="mt-desc">{r.description}</p> : null}
        {r.resolution ? (
          <div className="callout mt-resolution">
            <Icon name={r.stage === 'cancelled' ? 'x' : 'checkCircle'} size={16} />
            <div>
              <strong>{r.stage === 'cancelled' ? t('mt.why_cancelled') : t('mt.what_done')}</strong>
              <p>{r.resolution}</p>
            </div>
          </div>
        ) : null}
        {r.checklist.length ? (
          <section>
            <h3 className="mt-h3">
              {t('mt.checklist')} <span className="muted small">{r.checklist.filter((x) => x.done).length + '/' + r.checklist.length}</span>
            </h3>
            <ul className="mt-checks">
              {r.checklist.map((c, i) => (
                <li key={i}>
                  <label className={c.done ? 'done' : undefined}>
                    <input type="checkbox" checked={c.done} disabled={!work || !open} onChange={(e) => void tick(i, e.target.checked)} />
                    <span>{c.text}</span>
                  </label>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
        {work && open ? (
          <section className="mt-time">
            <h3 className="mt-h3">{t('mt.add_time')}</h3>
            <div className="mt-time-buttons">
              {[15, 30, 60, 120].map((m) => (
                <Button key={m} size="sm" icon="plus" disabled={busy} onClick={() => void time(m)}>
                  {hm(m)}
                </Button>
              ))}
            </div>
          </section>
        ) : null}
        <section>
          <h3 className="mt-h3">{t('mt.history')}</h3>
          <ol className="mt-events">
            {r.events.map((e, i) => (
              <li key={i}>
                <span className="mt-event-dot" aria-hidden="true" />
                <span>
                  <strong>{t(`mt.ev_${e.kind}` as MessageKey)}</strong>
                  {e.text ? <span> · {eventText(e.kind, e.text)}</span> : null}
                  <span className="muted small">
                    {' '}
                    · {e.by_name ?? t('mt.by_plan')} · {dateTime(e.at)}
                  </span>
                </span>
              </li>
            ))}
          </ol>
          <div className="mt-note-add">
            <input className="input" value={note} maxLength={300} placeholder={t('mt.note_placeholder')} aria-label={t('mt.note_placeholder')} onChange={(e) => setNote(e.target.value)} />
            <Button
              icon="note"
              disabled={!note.trim() || busy}
              onClick={() =>
                void run(async () => {
                  await action('maint.add_note', { id, note: note.trim() });
                  setNote('');
                  await reload();
                }, t('mt.note_added'))
              }
            >
              {t('mt.add_note')}
            </Button>
          </div>
        </section>
      </div>
      {ask ? (
        <MoveDialog
          stage={ask}
          onClose={() => setAsk(null)}
          onMove={async (n) => {
            setAsk(null);
            await move(ask, n);
          }}
        />
      ) : null}
    </Dialog>
  );
}

/** A stage change is stored as "done: what was done"; show the stage in words. */
function eventText(kind: string, text: string) {
  if (kind !== 'stage') return text;
  const [stage, ...rest] = text.split(': ');
  const label = STAGE[stage as Stage] ? t(`mt.stage_${stage}` as MessageKey) : stage;
  return rest.length ? `${label}: ${rest.join(': ')}` : label;
}

function RequestForm({ initial, onClose, onSaved }: { initial: Detail | null; onClose: () => void; onSaved: () => Promise<void> }) {
  const { data: c } = useAction<Choices>('maint.choices');
  const { busy, run } = useSave();
  const work = initial ? initial.can.work : true;
  const [f, setF] = useState({
    title: initial?.title ?? '',
    kind: initial?.kind ?? ('corrective' as Kind),
    priority: initial?.priority ?? ('normal' as Priority),
    equipment: initial?.equipment ?? '',
    place: initial?.equipment ? '' : (initial?.place ?? ''),
    stopped: initial?.stopped ?? false,
    team: initial?.team ?? '',
    technician: initial?.technician ?? '',
    scheduled: initial?.scheduled ?? '',
    duration: String(initial?.duration ?? 60),
    description: initial?.description ?? '',
    checklist: (initial?.checklist ?? []).map((x) => x.text).join('\n'),
    cost: initial?.cost ? plain(initial.cost) : '',
  });
  const [tried, setTried] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const cost = f.cost.trim() ? parseMoney(f.cost) : 0;
  const errors = {
    title: f.title.trim().length < 3 ? t('mt.e_title') : null,
    equipment: !f.equipment && !f.place.trim() ? t('mt.e_where') : null,
    duration: !/^\d{1,5}$/.test(f.duration) ? t('mt.e_minutes') : null,
    cost: cost === null || cost < 0 ? t('mt.e_money') : null,
  };
  const save = () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    void run(async () => {
      await action('maint.save_request', {
        id: initial?.id ?? null,
        title: f.title.trim(),
        kind: f.kind,
        priority: f.priority,
        equipment: f.equipment || null,
        place: f.equipment ? null : f.place.trim() || null,
        stopped: f.stopped,
        team: f.team || null,
        technician: f.technician || null,
        scheduled: f.scheduled || null,
        duration: Number(f.duration),
        description: f.description.trim() || null,
        checklist: f.checklist
          .split('\n')
          .map((x) => x.trim())
          .filter(Boolean),
        cost: cost ?? 0,
      });
      await onSaved();
      onClose();
    }, t('mt.request_saved'));
  };
  return (
    <Dialog
      title={initial ? t('mt.edit_request', { name: initial.name }) : t('mt.new_request')}
      onClose={onClose}
      size="lg"
      footer={
        <span className="foot-actions">
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} onClick={save}>
            {t('common.save')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        <Field label={t('mt.request_title')} error={tried ? errors.title : null} required>
          <input className="input" value={f.title} maxLength={120} onChange={(e) => set('title', e.target.value)} data-autofocus />
        </Field>
        <div className="hr-two">
          <Field label={t('mt.machine')} error={tried ? errors.equipment : null}>
            <select className="input" value={f.equipment} onChange={(e) => set('equipment', e.target.value)}>
              <option value="">{t('mt.no_machine')}</option>
              {(c?.equipment ?? []).map((e) => (
                <option key={e.id} value={e.id}>
                  {e.code ? `${e.name} (${e.code})` : e.name}
                </option>
              ))}
            </select>
          </Field>
          {!f.equipment ? (
            <Field label={t('mt.where')} error={tried ? errors.equipment : null}>
              <input className="input" value={f.place} maxLength={80} onChange={(e) => set('place', e.target.value)} />
            </Field>
          ) : work ? (
            <Field label={t('mt.kind')}>
              <select className="input" value={f.kind} onChange={(e) => set('kind', e.target.value as Kind)}>
                <option value="corrective">{t('mt.corrective')}</option>
                <option value="preventive">{t('mt.preventive')}</option>
              </select>
            </Field>
          ) : (
            <span />
          )}
        </div>
        <Field label={t('mt.priority')}>
          <PriorityPick value={f.priority} onChange={(p) => set('priority', p)} />
        </Field>
        <div className="setting-row">
          <div>
            <strong>{t('mt.stopped')}</strong>
            <p className="muted">{t('mt.stopped_hint')}</p>
          </div>
          <Switch checked={f.stopped} onChange={(x) => set('stopped', x)} label={t('mt.stopped')} />
        </div>
        {work ? (
          <>
            <div className="hr-two">
              <Field label={t('mt.team')}>
                <select className="input" value={f.team} onChange={(e) => set('team', e.target.value)}>
                  <option value="">—</option>
                  {(c?.teams ?? []).map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={t('mt.technician')}>
                <select className="input" value={f.technician} onChange={(e) => set('technician', e.target.value)}>
                  <option value="">{t('mt.nobody')}</option>
                  {(c?.workers ?? []).map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.name}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="hr-two">
              <Field label={t('mt.scheduled')}>
                <input className="input" type="date" value={f.scheduled} onChange={(e) => set('scheduled', e.target.value)} />
              </Field>
              <Field label={t('mt.planned_minutes')} error={tried ? errors.duration : null}>
                <input className="input" inputMode="numeric" value={f.duration} onChange={(e) => set('duration', e.target.value.replace(/\D/g, ''))} />
              </Field>
            </div>
          </>
        ) : null}
        <Field label={t('mt.details')}>
          <textarea className="input" rows={3} value={f.description} maxLength={2000} onChange={(e) => set('description', e.target.value)} />
        </Field>
        {work ? (
          <>
            <Field label={t('mt.checklist')} hint={t('mt.checklist_hint')}>
              <textarea className="input" rows={4} value={f.checklist} onChange={(e) => set('checklist', e.target.value)} />
            </Field>
            <Field label={t('mt.cost')} hint={t('mt.cost_hint')} error={tried ? errors.cost : null}>
              <input className="input" inputMode="decimal" value={f.cost} onChange={(e) => set('cost', e.target.value)} />
            </Field>
          </>
        ) : null}
      </div>
    </Dialog>
  );
}

// ---- equipment --------------------------------------------------------------------------

interface EquipmentRow {
  id: string;
  name: string;
  code: string | null;
  category: string | null;
  model: string | null;
  serial: string | null;
  place: string;
  location_name: string | null;
  team_name: string | null;
  technician_name: string | null;
  warranty_until: string | null;
  state: 'working' | 'retired';
  open: number;
  stopped: boolean;
  next_due: string | null;
  failures: number;
}

function EquipmentTab({ manage, onOpen }: { manage: boolean; onOpen: (id: string) => void }) {
  const [state, setState] = useState<'working' | 'retired' | 'all'>('working');
  const [category, setCategory] = useState('');
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [form, setForm] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setSearch(q.trim()), 250);
    return () => clearTimeout(id);
  }, [q]);
  const { data, reload } = useAction<{ today: string; equipment: EquipmentRow[] }>('maint.equipment_list', { state, category: category || null, search: search || null });
  const { data: c } = useAction<Choices>('maint.choices');
  return (
    <>
      <div className="toolbar mt-tools">
        <label className="search">
          <Icon name="search" size={16} />
          <input className="input" type="search" value={q} placeholder={t('mt.search_equipment')} aria-label={t('mt.search_equipment')} onChange={(e) => setQ(e.target.value)} />
        </label>
        <select className="input mt-select" aria-label={t('mt.category')} value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">{t('mt.all_categories')}</option>
          {(c?.categories ?? []).map((x) => (
            <option key={x} value={x}>
              {x}
            </option>
          ))}
        </select>
        <div className="segmented" role="radiogroup" aria-label={t('mt.state')}>
          {(['working', 'retired', 'all'] as const).map((s) => (
            <button key={s} type="button" role="radio" aria-checked={state === s} className={state === s ? 'active' : ''} onClick={() => setState(s)}>
              {t(`mt.eq_${s}` as MessageKey)}
            </button>
          ))}
        </div>
        {manage ? (
          <Button variant="primary" icon="plus" onClick={() => setForm(true)}>
            {t('mt.add_equipment')}
          </Button>
        ) : null}
      </div>
      {!data ? (
        <Skeleton h={300} r="var(--radius-lg)" />
      ) : !data.equipment.length && !search && !category && state === 'working' ? (
        <section className="panel">
          <EmptyState
            icon="wrench"
            title={t('mt.no_equipment')}
            body={t('mt.no_equipment_body')}
            action={
              manage ? (
                <Button variant="primary" icon="plus" onClick={() => setForm(true)}>
                  {t('mt.add_equipment')}
                </Button>
              ) : undefined
            }
          />
        </section>
      ) : (
        <section className="panel">
          <TableWrap>
            <Table
              view="maintenance.equipment"
              head={[t('mt.machine'), t('mt.where'), t('mt.technician'), t('mt.open_requests'), t('mt.next_preventive'), t('mt.warranty')]}
              phoneHide={[2, 4, 5]}
              empty={t('mt.no_match')}
              rows={data.equipment.map((e) => ({
                key: e.id,
                onClick: () => onOpen(e.id),
                cells: [
                  <span key="n" className="mt-eq-name">
                    <strong>{e.name}</strong>
                    <span className="muted small">{[e.code, e.category, e.model].filter(Boolean).join(' · ')}</span>
                    {e.stopped ? (
                      <span className="mt-stopped small">
                        <Icon name="alert" size={12} /> {t('mt.stopped_now')}
                      </span>
                    ) : e.state === 'retired' ? (
                      <span className="muted small">{t('mt.retired')}</span>
                    ) : null}
                  </span>,
                  [e.place, e.location_name].filter(Boolean).join(' · ') || '—',
                  e.technician_name ?? e.team_name ?? '—',
                  e.open ? <strong key="o">{e.open}</strong> : '—',
                  e.next_due ? <span className={e.next_due < data.today ? 'text-danger' : undefined}>{dayText(e.next_due)}</span> : '—',
                  e.warranty_until ? <span className={e.warranty_until < data.today ? 'muted' : undefined}>{dayText(e.warranty_until)}</span> : '—',
                ],
              }))}
            />
          </TableWrap>
        </section>
      )}
      {form ? <EquipmentForm initial={null} onClose={() => setForm(false)} onSaved={reload} /> : null}
    </>
  );
}

interface EquipmentFull {
  id: string;
  name: string;
  code: string | null;
  category: string | null;
  model: string | null;
  serial: string | null;
  location: string | null;
  location_name: string | null;
  place: string | null;
  team: string | null;
  team_name: string | null;
  technician: string | null;
  technician_name: string | null;
  vendor: string | null;
  vendor_phone: string | null;
  bought_on: string | null;
  warranty_until: string | null;
  state: 'working' | 'retired';
  retired_reason: string | null;
  note: string | null;
  today: string;
  requests: (Row & { resolution: string | null })[];
  plans: PlanRow[];
  stats: { failures: number; downtime: number; mttr: number | null; mtbf: number | null; cost: number; hours: number };
  can: { manage: boolean };
}

function EquipmentDialog({
  id,
  onClose,
  onChange,
  onOpen,
  onReport,
}: {
  id: string;
  onClose: () => void;
  onChange: () => Promise<void>;
  onOpen: (id: string) => void;
  onReport: (id: string) => void;
}) {
  const { data: e, reload } = useAction<EquipmentFull>('maint.equipment', { id });
  const [edit, setEdit] = useState(false);
  const [retire, setRetire] = useState(false);
  const [plan, setPlan] = useState<PlanRow | 'new' | null>(null);
  const { busy, run } = useSave();
  if (!e)
    return (
      <Dialog title={t('mt.machine')} onClose={onClose} size="xl">
        <Skeleton h={360} />
      </Dialog>
    );
  const refresh = async () => {
    await reload();
    await onChange();
  };
  if (edit) return <EquipmentForm initial={e} onClose={() => setEdit(false)} onSaved={refresh} />;
  const warrantyLeft = e.warranty_until && e.warranty_until >= e.today;
  return (
    <Dialog
      title={e.code ? `${e.name} (${e.code})` : e.name}
      onClose={onClose}
      size="xl"
      footer={
        <span className="foot-actions mt-foot">
          {e.can.manage ? (
            <Button
              variant="ghost"
              icon={e.state === 'retired' ? 'undo' : 'archive'}
              onClick={() =>
                e.state === 'retired'
                  ? void run(async () => {
                      await action('maint.retire_equipment', { id: e.id, retire: false });
                      await refresh();
                    }, t('mt.back_in_use'))
                  : setRetire(true)
              }
            >
              {e.state === 'retired' ? t('mt.bring_back') : t('mt.retire')}
            </Button>
          ) : null}
          <span className="mt-foot-moves">
            {e.can.manage ? (
              <Button variant="secondary" icon="edit" onClick={() => setEdit(true)}>
                {t('mt.edit')}
              </Button>
            ) : null}
            {e.state === 'working' ? (
              <Button variant="primary" icon="alert" onClick={() => onReport(e.id)}>
                {t('mt.report_problem')}
              </Button>
            ) : null}
          </span>
        </span>
      }
    >
      <div className="mt-detail">
        {e.state === 'retired' ? (
          <p className="callout">
            <Icon name="archive" size={16} /> {t('mt.retired_because', { reason: e.retired_reason ?? '—' })}
          </p>
        ) : null}
        <section className="rep-kpis mt-eq-kpis" aria-label={t('mt.last_year')}>
          <Kpi label={t('mt.k_failures')} value={String(e.stats.failures)} hint={t('mt.last_year')} />
          <Kpi label={t('mt.k_downtime')} value={hours(e.stats.downtime)} hint={t('mt.last_year')} warn={e.stats.downtime > 24} />
          <Kpi label={t('mt.k_mttr_short')} value={hours(e.stats.mttr)} hint={t('mt.k_mttr_eq')} />
          <Kpi label={t('mt.k_mtbf')} value={e.stats.mtbf === null ? '—' : tn('mt.days', Math.round(e.stats.mtbf))} hint={t('mt.k_mtbf_hint')} />
          <Kpi label={t('mt.k_cost')} value={money(e.stats.cost)} hint={t('mt.k_cost_hint', { hours: hours(e.stats.hours) })} />
        </section>
        <dl className="mt-facts">
          <div>
            <dt>{t('mt.where')}</dt>
            <dd>{[e.place, e.location_name].filter(Boolean).join(' · ') || '—'}</dd>
          </div>
          <div>
            <dt>{t('mt.category')}</dt>
            <dd>{e.category ?? '—'}</dd>
          </div>
          <div>
            <dt>{t('mt.model_serial')}</dt>
            <dd>{[e.model, e.serial].filter(Boolean).join(' · ') || '—'}</dd>
          </div>
          <div>
            <dt>{t('mt.looked_after')}</dt>
            <dd>{[e.technician_name, e.team_name].filter(Boolean).join(' · ') || '—'}</dd>
          </div>
          <div>
            <dt>{t('mt.vendor')}</dt>
            <dd>
              {e.vendor ?? '—'}
              {e.vendor_phone ? (
                <>
                  {' · '}
                  <a href={`tel:${e.vendor_phone.replace(/\s/g, '')}`}>{e.vendor_phone}</a>
                </>
              ) : null}
            </dd>
          </div>
          <div>
            <dt>{t('mt.warranty')}</dt>
            <dd className={warrantyLeft ? 'text-ok' : undefined}>
              {e.warranty_until ? (warrantyLeft ? t('mt.warranty_until', { day: dayText(e.warranty_until) }) : t('mt.warranty_ended', { day: dayText(e.warranty_until) })) : '—'}
            </dd>
          </div>
        </dl>
        {e.note ? <p className="mt-desc">{e.note}</p> : null}
        <section>
          <div className="mt-section-head">
            <h3 className="mt-h3">{t('mt.plans_of')}</h3>
            {e.can.manage && e.state === 'working' ? (
              <Button size="sm" icon="plus" onClick={() => setPlan('new')}>
                {t('mt.add_plan')}
              </Button>
            ) : null}
          </div>
          {e.plans.length ? (
            <ul className="mt-plan-list">
              {e.plans.map((p) => (
                <li key={p.id} className={p.active ? undefined : 'off'}>
                  <Icon name="calendar" size={16} />
                  <span>
                    <strong>{p.title}</strong>
                    <span className="muted small">
                      {' '}
                      · {tn('mt.every_days', p.every_days)} · {p.active ? t('mt.next_on', { day: dayText(p.next_due) }) : t('mt.plan_off')}
                    </span>
                  </span>
                  {e.can.manage ? <IconButton icon="edit" label={t('mt.edit_plan')} onClick={() => setPlan(p)} /> : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted small">{t('mt.no_plans_for')}</p>
          )}
        </section>
        <section>
          <h3 className="mt-h3">{t('mt.history')}</h3>
          <TableWrap>
            <Table
              head={[t('mt.request'), t('mt.stage'), t('mt.reported'), t('mt.downtime'), t('mt.time')]}
              phoneHide={[2, 3, 4]}
              empty={t('mt.no_history')}
              rows={e.requests.map((r) => ({
                key: r.id,
                onClick: () => onOpen(r.id),
                cells: [
                  <span key="t" className="mt-eq-name">
                    <strong>{r.title}</strong>
                    <span className="muted small">
                      {r.name} · {t(`mt.${r.kind}` as MessageKey)}
                    </span>
                  </span>,
                  <StatusBadge key="s" tone={STAGE[r.stage].tone} icon={STAGE[r.stage].icon} label={t(`mt.stage_${r.stage}` as MessageKey)} />,
                  dayText(r.created_at),
                  r.stopped ? hours(Math.round(((Date.parse(r.done_at ?? new Date().toISOString()) - Date.parse(r.created_at)) / 3_600_000) * 10) / 10) : '—',
                  r.spent ? hm(r.spent) : '—',
                ],
              }))}
            />
          </TableWrap>
        </section>
      </div>
      {retire ? (
        <RetireDialog
          name={e.name}
          busy={busy}
          onClose={() => setRetire(false)}
          onRetire={(reason) =>
            void run(async () => {
              await action('maint.retire_equipment', { id: e.id, retire: true, reason });
              setRetire(false);
              await refresh();
            }, t('mt.retired_done'))
          }
        />
      ) : null}
      {plan ? <PlanForm initial={plan === 'new' ? null : plan} equipment={e.id} onClose={() => setPlan(null)} onSaved={refresh} /> : null}
    </Dialog>
  );
}

function RetireDialog({ name, busy, onClose, onRetire }: { name: string; busy: boolean; onClose: () => void; onRetire: (reason: string) => void }) {
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  return (
    <Confirm
      title={t('mt.retire_title', { name })}
      confirm={t('mt.retire')}
      danger
      busy={busy}
      onClose={onClose}
      onConfirm={() => {
        setTried(true);
        if (reason.trim()) onRetire(reason.trim());
      }}
      body={
        <div className="form-stack">
          <p>{t('mt.retire_body')}</p>
          <Field label={t('mt.retire_reason')} error={tried && !reason.trim() ? t('mt.e_note') : null} required>
            <input className="input" value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} data-autofocus />
          </Field>
        </div>
      }
    />
  );
}

function EquipmentForm({ initial, onClose, onSaved }: { initial: EquipmentFull | null; onClose: () => void; onSaved: () => Promise<void> }) {
  const { data: c } = useAction<Choices>('maint.choices');
  const { busy, run } = useSave();
  const [f, setF] = useState({
    name: initial?.name ?? '',
    code: initial?.code ?? '',
    category: initial?.category ?? '',
    model: initial?.model ?? '',
    serial: initial?.serial ?? '',
    location: initial?.location ?? '',
    place: initial?.place ?? '',
    team: initial?.team ?? '',
    technician: initial?.technician ?? '',
    vendor: initial?.vendor ?? '',
    vendor_phone: initial?.vendor_phone ?? '',
    bought_on: initial?.bought_on ?? '',
    warranty_until: initial?.warranty_until ?? '',
    note: initial?.note ?? '',
  });
  const [tried, setTried] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const errors = {
    name: f.name.trim().length < 2 ? t('mt.e_name') : null,
    warranty: f.bought_on && f.warranty_until && f.warranty_until < f.bought_on ? t('mt.e_warranty') : null,
  };
  const save = () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    const s = (v: string) => v.trim() || null;
    void run(async () => {
      await action('maint.save_equipment', {
        id: initial?.id ?? null,
        name: f.name.trim(),
        code: s(f.code),
        category: s(f.category),
        model: s(f.model),
        serial: s(f.serial),
        location: f.location || null,
        place: s(f.place),
        team: f.team || null,
        technician: f.technician || null,
        vendor: s(f.vendor),
        vendor_phone: s(f.vendor_phone),
        bought_on: f.bought_on || null,
        warranty_until: f.warranty_until || null,
        note: s(f.note),
      });
      await onSaved();
      onClose();
    }, t('mt.equipment_saved'));
  };
  return (
    <Dialog
      title={initial ? t('mt.edit_equipment') : t('mt.add_equipment')}
      onClose={onClose}
      size="lg"
      footer={
        <span className="foot-actions">
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} onClick={save}>
            {t('common.save')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        <div className="hr-two">
          <Field label={t('mt.eq_name')} error={tried ? errors.name : null} required>
            <input className="input" value={f.name} maxLength={80} placeholder={t('mt.eq_name_example')} onChange={(e) => set('name', e.target.value)} data-autofocus />
          </Field>
          <Field label={t('mt.code')} hint={t('mt.code_hint')}>
            <input className="input" value={f.code} maxLength={30} onChange={(e) => set('code', e.target.value)} />
          </Field>
        </div>
        <div className="hr-two">
          <Field label={t('mt.category')}>
            <input className="input" list="mt-categories" value={f.category} maxLength={60} onChange={(e) => set('category', e.target.value)} />
            <datalist id="mt-categories">
              {(c?.categories ?? []).map((x) => (
                <option key={x} value={x} />
              ))}
            </datalist>
          </Field>
          <Field label={t('mt.model')}>
            <input className="input" value={f.model} maxLength={80} onChange={(e) => set('model', e.target.value)} />
          </Field>
        </div>
        <div className="hr-two">
          <Field label={t('mt.serial')}>
            <input className="input" value={f.serial} maxLength={60} onChange={(e) => set('serial', e.target.value)} />
          </Field>
          <Field label={t('mt.place')} hint={t('mt.place_hint')}>
            <input className="input" value={f.place} maxLength={80} onChange={(e) => set('place', e.target.value)} />
          </Field>
        </div>
        {(c?.locations.length ?? 0) > 1 ? (
          <Field label={t('mt.location')}>
            <select className="input" value={f.location} onChange={(e) => set('location', e.target.value)}>
              <option value="">—</option>
              {c!.locations.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
        <div className="hr-two">
          <Field label={t('mt.team')}>
            <select className="input" value={f.team} onChange={(e) => set('team', e.target.value)}>
              <option value="">—</option>
              {(c?.teams ?? []).map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('mt.technician')} hint={t('mt.technician_hint')}>
            <select className="input" value={f.technician} onChange={(e) => set('technician', e.target.value)}>
              <option value="">{t('mt.nobody')}</option>
              {(c?.workers ?? []).map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="hr-two">
          <Field label={t('mt.vendor')} hint={t('mt.vendor_hint')}>
            <input className="input" value={f.vendor} maxLength={80} onChange={(e) => set('vendor', e.target.value)} />
          </Field>
          <Field label={t('mt.vendor_phone')}>
            <input className="input" type="tel" value={f.vendor_phone} maxLength={40} onChange={(e) => set('vendor_phone', e.target.value)} />
          </Field>
        </div>
        <div className="hr-two">
          <Field label={t('mt.bought_on')}>
            <input className="input" type="date" value={f.bought_on} onChange={(e) => set('bought_on', e.target.value)} />
          </Field>
          <Field label={t('mt.warranty_end')} error={tried ? errors.warranty : null}>
            <input className="input" type="date" value={f.warranty_until} onChange={(e) => set('warranty_until', e.target.value)} />
          </Field>
        </div>
        <Field label={t('mt.note')}>
          <textarea className="input" rows={2} value={f.note} maxLength={2000} onChange={(e) => set('note', e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

// ---- preventive plans -------------------------------------------------------------------

interface PlanRow {
  id: string;
  equipment: string;
  equipment_name?: string;
  title: string;
  every_days: number;
  next_due: string;
  lead_days: number;
  team: string | null;
  team_name?: string | null;
  technician: string | null;
  technician_name: string | null;
  duration: number;
  checklist: string[];
  active: boolean;
  last_done?: string | null;
}

function PlansTab({ manage, onChange }: { manage: boolean; onChange: () => Promise<void> }) {
  const { data, reload } = useAction<{ today: string; plans: PlanRow[] }>('maint.plans');
  const [form, setForm] = useState<PlanRow | 'new' | null>(null);
  if (!data) return <Skeleton h={300} r="var(--radius-lg)" />;
  const active = data.plans.filter((p) => p.active);
  const late = active.filter((p) => p.next_due < data.today).length;
  const soon = active.filter((p) => p.next_due >= data.today && p.next_due <= addDaysIso(data.today, 7)).length;
  return (
    <>
      <section className="rep-kpis" aria-label={t('mt.plans_glance')}>
        <Kpi label={t('mt.k_plans')} value={String(active.length)} hint={t('mt.k_plans_hint')} />
        <Kpi label={t('mt.k_plans_week')} value={String(soon)} hint={t('mt.k_plans_week_hint')} />
        <Kpi label={t('mt.k_plans_late')} value={String(late)} hint={t('mt.k_plans_late_hint')} warn={late > 0} />
      </section>
      <div className="toolbar mt-tools">
        <span className="muted small">{t('mt.plans_explain')}</span>
        {manage ? (
          <Button variant="primary" icon="plus" onClick={() => setForm('new')}>
            {t('mt.add_plan')}
          </Button>
        ) : null}
      </div>
      {!data.plans.length ? (
        <section className="panel">
          <EmptyState icon="calendar" title={t('mt.no_plans')} body={t('mt.no_plans_body')} />
        </section>
      ) : (
        <section className="panel">
          <TableWrap>
            <Table
              view="maintenance.plans"
              head={[t('mt.plan'), t('mt.machine'), t('mt.how_often'), t('mt.next_due'), t('mt.last_done'), t('mt.technician')]}
              phoneHide={[2, 4, 5]}
              rows={data.plans.map((p) => ({
                key: p.id,
                onClick: manage ? () => setForm(p) : undefined,
                cells: [
                  <span key="t" className="mt-eq-name">
                    <strong>{p.title}</strong>
                    {p.checklist.length ? <span className="muted small">{tn('mt.checks_n', p.checklist.length)}</span> : null}
                    {!p.active ? <span className="muted small">{t('mt.plan_off')}</span> : null}
                  </span>,
                  p.equipment_name ?? '—',
                  tn('mt.every_days', p.every_days),
                  p.active ? (
                    <span key="d" className={p.next_due < data.today ? 'text-danger' : p.next_due <= addDaysIso(data.today, 7) ? 'text-warn' : undefined}>
                      {dayText(p.next_due)}
                    </span>
                  ) : (
                    '—'
                  ),
                  p.last_done ? dateTime(p.last_done).replace(/,.*$/, '') : '—',
                  p.technician_name ?? p.team_name ?? '—',
                ],
              }))}
            />
          </TableWrap>
        </section>
      )}
      {form ? (
        <PlanForm
          initial={form === 'new' ? null : form}
          onClose={() => setForm(null)}
          onSaved={async () => {
            await reload();
            await onChange();
          }}
        />
      ) : null}
    </>
  );
}

function PlanForm({ initial, equipment, onClose, onSaved }: { initial: PlanRow | null; equipment?: string; onClose: () => void; onSaved: () => Promise<void> }) {
  const { data: c } = useAction<Choices>('maint.choices');
  const { busy, run } = useSave();
  const [f, setF] = useState({
    equipment: initial?.equipment ?? equipment ?? '',
    title: initial?.title ?? '',
    every_days: String(initial?.every_days ?? 30),
    next_due: initial?.next_due ?? '',
    lead_days: String(initial?.lead_days ?? 7),
    team: initial?.team ?? '',
    technician: initial?.technician ?? '',
    duration: String(initial?.duration ?? 60),
    checklist: (initial?.checklist ?? []).join('\n'),
    active: initial?.active ?? true,
  });
  const [tried, setTried] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const errors = {
    equipment: !f.equipment ? t('mt.e_machine') : null,
    title: f.title.trim().length < 3 ? t('mt.e_title') : null,
    every: !/^\d{1,4}$/.test(f.every_days) || Number(f.every_days) < 1 ? t('mt.e_every') : null,
    next: !f.next_due ? t('mt.e_next') : null,
    duration: !/^\d{1,5}$/.test(f.duration) ? t('mt.e_minutes') : null,
    lead: !/^\d{1,2}$/.test(f.lead_days) ? t('mt.e_lead') : null,
  };
  const presets = [7, 14, 30, 90, 180, 365];
  const save = () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    void run(async () => {
      await action('maint.save_plan', {
        id: initial?.id ?? null,
        equipment: f.equipment,
        title: f.title.trim(),
        every_days: Number(f.every_days),
        next_due: f.next_due,
        lead_days: Number(f.lead_days),
        team: f.team || null,
        technician: f.technician || null,
        duration: Number(f.duration),
        checklist: f.checklist
          .split('\n')
          .map((x) => x.trim())
          .filter(Boolean),
        active: f.active,
      });
      await onSaved();
      onClose();
    }, t('mt.plan_saved'));
  };
  return (
    <Dialog
      title={initial ? t('mt.edit_plan') : t('mt.add_plan')}
      onClose={onClose}
      size="lg"
      footer={
        <span className="foot-actions">
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} onClick={save}>
            {t('common.save')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        <Field label={t('mt.machine')} error={tried ? errors.equipment : null} required>
          <select className="input" value={f.equipment} disabled={!!equipment} onChange={(e) => set('equipment', e.target.value)}>
            <option value="">{t('mt.choose_machine')}</option>
            {(c?.equipment ?? []).map((e) => (
              <option key={e.id} value={e.id}>
                {e.code ? `${e.name} (${e.code})` : e.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('mt.plan_title')} error={tried ? errors.title : null} required>
          <input className="input" value={f.title} maxLength={120} placeholder={t('mt.plan_title_example')} onChange={(e) => set('title', e.target.value)} data-autofocus />
        </Field>
        <Field label={t('mt.how_often')} hint={t('mt.how_often_hint')} error={tried ? errors.every : null} required>
          <div className="mt-every">
            <div className="mt-presets" role="group" aria-label={t('mt.how_often')}>
              {presets.map((n) => (
                <button
                  key={n}
                  type="button"
                  aria-pressed={f.every_days === String(n)}
                  className={`lu-day-pill${f.every_days === String(n) ? ' on' : ''}`}
                  onClick={() => set('every_days', String(n))}
                >
                  {t(`mt.preset_${n}` as MessageKey)}
                </button>
              ))}
            </div>
            <span className="mt-every-days">
              <input className="input" inputMode="numeric" value={f.every_days} aria-label={t('mt.every_n_days')} onChange={(e) => set('every_days', e.target.value.replace(/\D/g, ''))} />
              <span className="muted">{t('mt.days_word')}</span>
            </span>
          </div>
        </Field>
        <div className="hr-two">
          <Field label={t('mt.next_due')} hint={t('mt.next_due_hint')} error={tried ? errors.next : null} required>
            <input className="input" type="date" value={f.next_due} onChange={(e) => set('next_due', e.target.value)} />
          </Field>
          <Field label={t('mt.lead_days')} hint={t('mt.lead_days_hint')} error={tried ? errors.lead : null}>
            <input className="input" inputMode="numeric" value={f.lead_days} onChange={(e) => set('lead_days', e.target.value.replace(/\D/g, ''))} />
          </Field>
        </div>
        <div className="hr-two">
          <Field label={t('mt.team')}>
            <select className="input" value={f.team} onChange={(e) => set('team', e.target.value)}>
              <option value="">{t('mt.machine_team')}</option>
              {(c?.teams ?? []).map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('mt.technician')}>
            <select className="input" value={f.technician} onChange={(e) => set('technician', e.target.value)}>
              <option value="">{t('mt.machine_technician')}</option>
              {(c?.workers ?? []).map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label={t('mt.planned_minutes')} error={tried ? errors.duration : null}>
          <input className="input" inputMode="numeric" value={f.duration} onChange={(e) => set('duration', e.target.value.replace(/\D/g, ''))} />
        </Field>
        <Field label={t('mt.checklist')} hint={t('mt.checklist_hint')}>
          <textarea className="input" rows={4} value={f.checklist} onChange={(e) => set('checklist', e.target.value)} />
        </Field>
        {initial ? (
          <div className="setting-row">
            <div>
              <strong>{t('mt.plan_active')}</strong>
              <p className="muted">{t('mt.plan_active_hint')}</p>
            </div>
            <Switch checked={f.active} onChange={(x) => set('active', x)} label={t('mt.plan_active')} />
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}

// ---- calendar ---------------------------------------------------------------------------

function CalendarTab({ today, onOpen }: { today: string; onOpen: (id: string) => void }) {
  const [month, setMonth] = useState(today.slice(0, 7));
  const { data } = useAction<{
    today: string;
    from: string;
    to: string;
    requests: Row[];
    upcoming: { plan: string; title: string; equipment_name: string; technician_name: string | null; day: string; duration: number }[];
  }>('maint.calendar', { month });
  const shift = (n: number) => {
    const d = new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5, 7)) - 1 + n, 1));
    setMonth(d.toISOString().slice(0, 7));
  };
  const title = new Intl.DateTimeFormat(locale(), { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${month}-01T00:00:00Z`));
  const first = new Date(`${month}-01T00:00:00Z`);
  const lead = (first.getUTCDay() + 6) % 7;
  const days = data ? Math.round((Date.parse(`${data.to}T00:00:00Z`) - first.getTime()) / 86_400_000) + 1 : 0;
  const cells = Array.from({ length: Math.ceil((lead + days) / 7) * 7 }, (_, i) => (i < lead || i >= lead + days ? null : addDaysIso(`${month}-01`, i - lead)));
  const weekdays = Array.from({ length: 7 }, (_, i) => new Intl.DateTimeFormat(locale(), { weekday: 'short', timeZone: 'UTC' }).format(new Date(Date.UTC(2026, 9, 26 + i))));
  const on = (d: string) => ({ reqs: data?.requests.filter((r) => r.scheduled === d) ?? [], plans: data?.upcoming.filter((u) => u.day === d) ?? [] });
  const listDays = cells.filter((d): d is string => !!d && (on(d).reqs.length > 0 || on(d).plans.length > 0));
  return (
    <section className="panel mt-cal">
      <div className="mt-cal-head">
        <IconButton icon="back" label={t('mt.prev_month')} onClick={() => shift(-1)} />
        <h2>{title}</h2>
        <IconButton icon="chevronRight" label={t('mt.next_month')} onClick={() => shift(1)} />
        {month !== today.slice(0, 7) ? (
          <Button size="sm" variant="ghost" onClick={() => setMonth(today.slice(0, 7))}>
            {t('mt.this_month')}
          </Button>
        ) : null}
        <span className="mt-cal-legend small muted">
          <span className="mt-ev mt-ev-req" /> {t('mt.legend_request')}
          <span className="mt-ev mt-ev-plan" /> {t('mt.legend_plan')}
        </span>
      </div>
      {!data ? (
        <Skeleton h={420} />
      ) : (
        <>
          <div className="mt-cal-grid" role="grid" aria-label={title}>
            {weekdays.map((w) => (
              <div key={w} className="mt-cal-wd" role="columnheader">
                {w}
              </div>
            ))}
            {cells.map((d, i) => {
              if (!d) return <div key={i} className="mt-cal-day mt-blank" />;
              const { reqs, plans } = on(d);
              return (
                <div key={d} className={`mt-cal-day${d === today ? ' today' : ''}${d < today ? ' past' : ''}`} role="gridcell">
                  <span className="mt-cal-n">{Number(d.slice(8))}</span>
                  {reqs.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      className={`mt-ev mt-ev-req st-${r.stage}${r.priority === 'urgent' ? ' urgent' : ''}`}
                      onClick={() => onOpen(r.id)}
                      title={`${r.name} · ${r.title}`}
                    >
                      {r.title}
                    </button>
                  ))}
                  {plans.map((p) => (
                    <span key={p.plan} className="mt-ev mt-ev-plan" title={`${p.title} · ${p.equipment_name}`}>
                      {p.title}
                    </span>
                  ))}
                </div>
              );
            })}
          </div>
          <ol className="mt-cal-list">
            {listDays.length ? (
              listDays.map((d) => (
                <li key={d}>
                  <strong className={d === today ? 'mt-today' : undefined}>{dayText(d)}</strong>
                  <ul>
                    {on(d).reqs.map((r) => (
                      <li key={r.id}>
                        <button type="button" className="link" onClick={() => onOpen(r.id)}>
                          {r.name} · {r.title}
                        </button>
                        <span className="muted small"> · {r.equipment_name ?? r.place}</span>
                      </li>
                    ))}
                    {on(d).plans.map((p) => (
                      <li key={p.plan} className="muted">
                        {p.title} · {p.equipment_name} ({t('mt.legend_plan').toLowerCase()})
                      </li>
                    ))}
                  </ul>
                </li>
              ))
            ) : (
              <li className="muted">{t('mt.month_empty')}</li>
            )}
          </ol>
        </>
      )}
    </section>
  );
}

// ---- teams ------------------------------------------------------------------------------

interface TeamRow {
  id: string;
  name: string;
  active: boolean;
  open: number;
  equipment: number;
  members: { id: string; name: string }[];
}

function TeamsTab({ onChange }: { onChange: () => Promise<void> }) {
  const { data, reload } = useAction<{ teams: TeamRow[]; workers: { id: string; name: string }[] }>('maint.teams');
  const [form, setForm] = useState<TeamRow | 'new' | null>(null);
  if (!data) return <Skeleton h={240} r="var(--radius-lg)" />;
  return (
    <>
      <div className="toolbar mt-tools">
        <span className="muted small">{t('mt.teams_explain')}</span>
        <Button variant="primary" icon="plus" onClick={() => setForm('new')}>
          {t('mt.add_team')}
        </Button>
      </div>
      {!data.teams.length ? (
        <section className="panel">
          <EmptyState icon="users" title={t('mt.no_teams')} body={t('mt.no_teams_body')} />
        </section>
      ) : (
        <div className="mt-teams">
          {data.teams.map((x) => (
            <section key={x.id} className={`panel mt-team${x.active ? '' : ' off'}`}>
              <div className="mt-section-head">
                <h2>{x.name}</h2>
                <IconButton icon="edit" label={t('mt.edit_team')} onClick={() => setForm(x)} />
              </div>
              <p className="muted small">
                {tn('mt.team_open', x.open)} · {tn('mt.team_machines', x.equipment)}
                {!x.active ? ` · ${t('mt.team_off')}` : ''}
              </p>
              <ul className="mt-members">
                {x.members.length ? (
                  x.members.map((m) => (
                    <li key={m.id}>
                      <span className="tk-avatar">{initials(m.name)}</span> {m.name}
                    </li>
                  ))
                ) : (
                  <li className="muted small">{t('mt.no_members')}</li>
                )}
              </ul>
            </section>
          ))}
        </div>
      )}
      {form ? (
        <TeamForm
          initial={form === 'new' ? null : form}
          workers={data.workers}
          onClose={() => setForm(null)}
          onSaved={async () => {
            await reload();
            await onChange();
          }}
        />
      ) : null}
    </>
  );
}

function TeamForm({ initial, workers, onClose, onSaved }: { initial: TeamRow | null; workers: { id: string; name: string }[]; onClose: () => void; onSaved: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [name, setName] = useState(initial?.name ?? '');
  const [members, setMembers] = useState<string[]>(initial?.members.map((m) => m.id) ?? []);
  const [active, setActive] = useState(initial?.active ?? true);
  const [tried, setTried] = useState(false);
  const save = () => {
    setTried(true);
    if (name.trim().length < 2) return;
    void run(async () => {
      await action('maint.save_team', { id: initial?.id ?? null, name: name.trim(), members, active });
      await onSaved();
      onClose();
    }, t('mt.team_saved'));
  };
  return (
    <Dialog
      title={initial ? t('mt.edit_team') : t('mt.add_team')}
      onClose={onClose}
      size="md"
      footer={
        <span className="foot-actions">
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} onClick={save}>
            {t('common.save')}
          </Button>
        </span>
      }
    >
      <div className="form-stack">
        <Field label={t('mt.team_name')} error={tried && name.trim().length < 2 ? t('mt.e_name') : null} required>
          <input className="input" value={name} maxLength={60} placeholder={t('mt.team_name_example')} onChange={(e) => setName(e.target.value)} data-autofocus />
        </Field>
        <Field label={t('mt.members')} hint={t('mt.members_hint')}>
          <div className="lu-days mt-member-pick" role="group" aria-label={t('mt.members')}>
            {workers.map((w) => {
              const on = members.includes(w.id);
              return (
                <button key={w.id} type="button" aria-pressed={on} className={`lu-day-pill${on ? ' on' : ''}`} onClick={() => setMembers(on ? members.filter((x) => x !== w.id) : [...members, w.id])}>
                  {w.name}
                </button>
              );
            })}
          </div>
        </Field>
        {initial ? (
          <div className="setting-row">
            <div>
              <strong>{t('mt.team_active')}</strong>
              <p className="muted">{t('mt.team_active_hint')}</p>
            </div>
            <Switch checked={active} onChange={setActive} label={t('mt.team_active')} />
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}

// ---- report -----------------------------------------------------------------------------

function ReportTab({ today }: { today: string }) {
  const [month, setMonth] = useState(today.slice(0, 7));
  const from = `${month}-01`;
  const last = new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0)).toISOString().slice(0, 10);
  const to = last > today ? today : last;
  const { data } = useAction<{
    machines: { id: string; name: string; done: number; corrective: number; preventive: number; minutes: number; cost: number; downtime: number }[];
    people: { id: string; name: string; done: number; minutes: number }[];
    total: { done: number; preventive: number; minutes: number; cost: number; on_time: number };
  }>('maint.report', { from, to: to < from ? from : to });
  const tot = data?.total;
  return (
    <div className="lu-report">
      <div className="lu-day-tools">
        <input className="input lu-date" type="month" value={month} max={today.slice(0, 7)} onChange={(e) => e.target.value && setMonth(e.target.value)} aria-label={t('mt.month')} />
        <Button
          size="sm"
          icon="download"
          disabled={!data?.machines.length}
          onClick={() =>
            data &&
            downloadCsv(
              `maintenance-${month}.csv`,
              [t('mt.machine'), t('mt.done_n'), t('mt.corrective'), t('mt.preventive'), t('mt.downtime_h'), t('mt.hours_spent'), t('mt.cost')],
              data.machines.map((m) => [m.name, m.done, m.corrective, m.preventive, m.downtime, Math.round(m.minutes / 6) / 10, plain(m.cost)]),
            )
          }
        >
          {t('mt.csv')}
        </Button>
      </div>
      {!data || !tot ? (
        <Skeleton h={240} r="var(--radius-lg)" />
      ) : (
        <>
          <section className="rep-kpis" aria-label={t('mt.month')}>
            <Kpi label={t('mt.k_r_done')} value={String(tot.done)} hint={tn('mt.k_r_preventive', tot.preventive)} />
            <Kpi
              label={t('mt.k_r_on_time')}
              value={tot.preventive ? `${Math.round((tot.on_time / tot.preventive) * 100)}%` : '—'}
              hint={t('mt.k_r_on_time_hint')}
              warn={!!tot.preventive && tot.on_time / tot.preventive < 0.8}
            />
            <Kpi label={t('mt.k_r_hours')} value={hours(Math.round(tot.minutes / 6) / 10)} hint={t('mt.k_r_hours_hint')} />
            <Kpi label={t('mt.k_r_cost')} value={money(tot.cost)} hint={t('mt.k_r_cost_hint')} />
          </section>
          <div className="lu-report-grid mt-report-grid">
            <section className="panel">
              <h2>{t('mt.by_machine')}</h2>
              <TableWrap>
                <Table
                  head={[t('mt.machine'), t('mt.done_n'), t('mt.downtime_h'), t('mt.hours_spent'), t('mt.cost')]}
                  phoneHide={[3, 4]}
                  empty={t('mt.no_done_month')}
                  rows={data.machines.map((m) => ({
                    key: m.id,
                    cells: [
                      <span key="n" className="mt-eq-name">
                        <strong>{m.name}</strong>
                        <span className="muted small">{t('mt.split', { c: m.corrective, p: m.preventive })}</span>
                      </span>,
                      String(m.done),
                      m.downtime ? hours(m.downtime) : '—',
                      hours(Math.round(m.minutes / 6) / 10),
                      money(m.cost),
                    ],
                  }))}
                />
              </TableWrap>
            </section>
            <section className="panel">
              <h2>{t('mt.by_person')}</h2>
              <TableWrap>
                <Table
                  head={[t('mt.technician'), t('mt.done_n'), t('mt.hours_spent')]}
                  empty={t('mt.no_done_month')}
                  rows={data.people.map((p) => ({ key: p.id, cells: [p.name, String(p.done), hours(Math.round(p.minutes / 6) / 10)] }))}
                />
              </TableWrap>
            </section>
          </div>
        </>
      )}
    </div>
  );
}
